import React, { useState } from 'react';
import { FuelButton, FuelTooltip, FuelTypography } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import ShieldLock from '@assets/svg/shieldLock.svg';
import { formatPhone } from '@utils/formatters';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import useGlobals from '@hooks/useGlobals';
import { getTextToContactTruckerWhatsApp } from '@helpers/journeys/whatsappTalk';
import WhatsAppButton from '../../WhatsAppButton';
import Toast from '../../Toast';
import * as Styled from './TruckerInfoStyles';
import { TruckerInfoProps } from './TruckerInfoTypes';
import TruckerAvatar from '../TruckerAvatar/TruckerAvatar';

function TruckerInfo({
  avatar,
  name,
  phone,
  plate,
  notification = { isShow: false, total: 0 },
  onCallbackCopyPhone,
  onCallbackSentWhatsApp,
  depositFee,
  showIn,
  journeyId,
}: TruckerInfoProps) {
  const { user } = useGlobals();
  const fullName = name.split(' ');
  const firstName = fullName ? fullName[0] : '';
  const lastName = fullName.length > 1 ? fullName[fullName.length - 1] : '';
  const messageWhatsApp = getTextToContactTruckerWhatsApp({
    truckerName: firstName,
    companyName: user?.data?.company?.name || '',
    userName: user?.data?.name.split(' ')[0] || '',
    type: showIn,
    id: journeyId,
  });
  const [copiedMessage, copy] = useCopyToClipboard(
    'Telefone copiado com sucesso!',
  );
  const [isShowToast, setIsShowToast] = useState(false);

  function handlePrintTruckerName() {
    let nameAndSurname = `${firstName}`;

    if (lastName) {
      nameAndSurname = `${firstName} ${lastName}`;
    }

    nameAndSurname = nameAndSurname.toLowerCase();

    return (
      <FuelTypography
        tag="p"
        size="body-sm"
        color="neutral-strong"
        weight="bold"
      >
        {nameAndSurname}
      </FuelTypography>
    );
  }

  function handlePrintActions() {
    if (!phone?.showButton) {
      return null;
    }

    const actions = [
      <Styled.WrapperAlignRigth
        key={`truckerInfo-${firstName}-button-whatsApp`}
      >
        <WhatsAppButton
          phone={phone.number}
          message={messageWhatsApp}
          onCallbackSentWhatsApp={onCallbackSentWhatsApp}
          hasButton
        />
      </Styled.WrapperAlignRigth>,
    ];

    if (phone.number) {
      // remove o código do pais
      let phoneFormatted = phone.number.slice(2);
      phoneFormatted = formatPhone(phoneFormatted);

      // Se tiver telefone exibe o botão de copiar
      actions.push(
        <Styled.WrapperNewLineMobile
          key={`truckerInfo-${firstName}-button-copy-phone`}
        >
          <FuelButton
            data-testid="button-copy-phone"
            variant="secondary"
            size="small"
            trailingIcon="duplicate"
            onClick={() => {
              copy(phoneFormatted);

              setIsShowToast(true);

              if (onCallbackCopyPhone) {
                onCallbackCopyPhone(phoneFormatted);
              }
            }}
          >
            {phoneFormatted}
          </FuelButton>
        </Styled.WrapperNewLineMobile>,
      );
    }

    return <>{actions.map((action) => action)}</>;
  }

  function handlePrintPlate() {
    if (!plate) {
      return null;
    }

    return (
      <FuelTypography
        tag="p"
        size="caption"
        color="neutral-strong"
        weight="medium"
      >
        {plate}
      </FuelTypography>
    );
  }

  function handlePrintDepositFee() {
    if (!depositFee?.tagLabel) {
      return null;
    }

    return (
      <FuelTooltip
        placement="bottom"
        label="Esse motorista pagou o Frete Seguro"
        content="Garantias para o motorista e cobertura em caso de roubo da carga."
        size="sm"
      >
        <FuelFlex defaultProps={{ gap: 'km1', alignItems: 'center' }}>
          <Styled.ShieldLockIcon
            src={ShieldLock}
            data-testid="fuelTagDepositFee"
          />
          <FuelTypography
            size="caption"
            color="neutral-default"
            weight="medium"
          >
            Pagou Frete Seguro
          </FuelTypography>
        </FuelFlex>
      </FuelTooltip>
    );
  }

  return (
    <Styled.Content>
      <FuelFlex defaultProps={{ alignItems: 'center', gap: 'km3' }}>
        <TruckerAvatar
          name={fullName}
          photo={avatar}
          notification={notification}
        />
        <FuelFlex defaultProps={{ flexDirection: 'column', gap: 'km1' }}>
          {handlePrintTruckerName()}
          {handlePrintPlate()}
          {handlePrintDepositFee()}
        </FuelFlex>
      </FuelFlex>
      {handlePrintActions()}
      <Toast
        isShow={isShowToast}
        title={copiedMessage}
        slug="trucker_info"
        onClose={() => setIsShowToast(false)}
      />
    </Styled.Content>
  );
}

export default TruckerInfo;
